// admin-client/src/lib/api.js
// Centralised fetch wrapper. All requests use credentials:'include' so the
// HttpOnly auth cookie is sent.

const BASE = '/api';

async function request(path, { method = 'GET', body, headers = {}, isFormData = false } = {}) {
  const opts = {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'fetch',
      ...headers,
    },
  };
  if (body !== undefined) {
    if (isFormData) {
      opts.body = body; // FormData — browser sets multipart boundary
    } else {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
  }
  const res = await fetch(BASE + path, opts);
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const api = {
  auth: {
    status: () => request('/auth/status'),
    setup: (body) => request('/auth/setup', { method: 'POST', body }),
    login: (body) => request('/auth/login', { method: 'POST', body }),
    me: () => request('/auth/me'),
    updateMe: (body) => request('/auth/me', { method: 'PUT', body }),
    logout: () => request('/auth/logout', { method: 'POST' }),
  },
  mfa: {
    setup: () => request('/mfa/setup', { method: 'POST' }),
    verifySetup: (code) => request('/mfa/verify-setup', { method: 'POST', body: { code } }),
    disable: (code) => request('/mfa/disable', { method: 'POST', body: { code } }),
    loginVerify: (code) => request('/mfa/login-verify', { method: 'POST', body: { code } }),
  },
  passkey: {
    config: () => request('/passkeys/config'),
    registerOptions: () => request('/passkeys/register-options', { method: 'POST' }),
    registerVerify: (body) => request('/passkeys/register-verify', { method: 'POST', body }),
    list: () => request('/passkeys/list'),
    delete: (id) => request(`/passkeys/${id}`, { method: 'DELETE' }),
    loginOptions: (email) => request('/passkeys/login-options', { method: 'POST', body: { email } }),
    loginVerify: (body) => request('/passkeys/login-verify', { method: 'POST', body }),
  },
  users: {
    list: () => request('/users'),
    create: (body) => request('/users', { method: 'POST', body }),
    patch: (id, body) => request(`/users/${id}`, { method: 'PATCH', body }),
    resetPassword: (id) => request(`/users/${id}/reset-password`, { method: 'POST' }),
    clearLockout: (id) => request(`/users/${id}/clear-lockout`, { method: 'POST' }),
    disableMfa: (id) => request(`/users/${id}/disable-mfa`, { method: 'POST' }),
    passkeys: (id) => request(`/users/${id}/passkeys`),
    delete: (id) => request(`/users/${id}`, { method: 'DELETE' }),
    accountSetup: (body) => request('/users/account-setup', { method: 'POST', body }),
  },
  devices: {
    list: () => request('/devices'),
    create: (body) => request('/devices', { method: 'POST', body }),
    patch: (id, body) => request(`/devices/${id}`, { method: 'PATCH', body }),
    delete: (id, { cascade = false } = {}) =>
      request(`/devices/${id}${cascade ? '?cascade=true' : ''}`, { method: 'DELETE' }),
    regenerate: (id) => request(`/devices/${id}/regenerate-code`, { method: 'POST' }),
    revoke: (id) => request(`/devices/${id}/revoke`, { method: 'POST' }),
    /** All distinct source IPs each iPad has authenticated from (recent first). */
    ipLog: () => request('/devices/ip-log'),
    /** IP access control: known (trusted) IPs, blocked IPs, and odd IPs. */
    ipAccess: () => request('/devices/ip-access'),
    addKnownIp: (ip, label) => request('/devices/known-ips', { method: 'POST', body: { ip, label } }),
    removeKnownIp: (ip) => request(`/devices/known-ips/${encodeURIComponent(ip)}`, { method: 'DELETE' }),
    blockIp: (ip, reason) => request('/devices/blocked-ips', { method: 'POST', body: { ip, reason } }),
    unblockIp: (ip) => request(`/devices/blocked-ips/${encodeURIComponent(ip)}`, { method: 'DELETE' }),
    /**
     * Generate/regenerate a 5-char kiosk_token that lives in the iPad's
     * Home Screen URL forever (?t=XXXXX). Used as a recovery path when
     * iOS evicts the PWA's IDB storage. Returns the updated device row
     * with the new kiosk_token populated.
     */
    kioskToken: (id) => request(`/devices/${id}/kiosk-token`, { method: 'POST' }),
    /**
     * Enable/disable a device's kiosk token without regenerating it.
     * Disabling makes /devices/kiosk-bootstrap refuse the URL (403)
     * until re-enabled. Returns { device } with kiosk_token_active set.
     */
    kioskTokenSetActive: (id, active) =>
      request(`/devices/${id}/kiosk-token`, { method: 'PATCH', body: { active } }),
    /**
     * Phase-2 mTLS: download a .mobileconfig profile for the given device
     * and surface the PKCS#12 password (returned in the X-IPTBC-P12-Password
     * header). The admin AirDrops the profile to the iPad and types the
     * password during install.
     *
     * Bypasses the normal `request()` wrapper because we need response
     * headers AND a binary body.
     */
    certProfile: async (id) => {
      const res = await fetch(`/api/devices/${encodeURIComponent(id)}/cert-profile`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Accept': 'application/x-apple-aspen-config', 'X-Requested-With': 'fetch' },
      });
      if (!res.ok) {
        const text = await res.text();
        const e = new Error(text || `HTTP ${res.status}`);
        e.status = res.status;
        throw e;
      }
      const p12Password = res.headers.get('X-IPTBC-P12-Password') || '';
      const blob = await res.blob();
      return { blob, p12Password };
    },
  },
  etechlog: {
    // eTechlog iPads (the devices being tracked)
    devicesList: () => request('/etechlog/devices'),
    devicesCreate: (body) => request('/etechlog/devices', { method: 'POST', body }),
    devicesPatch: (id, body) => request(`/etechlog/devices/${id}`, { method: 'PATCH', body }),
    devicesRegenerateQr: (id) =>
      request(`/etechlog/devices/${id}/regenerate-qr`, { method: 'POST' }),
    devicesDelete: (id) => request(`/etechlog/devices/${id}`, { method: 'DELETE' }),
    deviceQrPngUrl: (id) => `/api/etechlog/devices/${encodeURIComponent(id)}/qr.png`,

    // Tracker iPads (the instructor-room iPads running the scanner PWA)
    trackersList: () => request('/etechlog/trackers'),
    trackersCreate: (body) => request('/etechlog/trackers', { method: 'POST', body }),
    trackersPatch: (id, body) => request(`/etechlog/trackers/${id}`, { method: 'PATCH', body }),
    trackersRegenerateCode: (id) =>
      request(`/etechlog/trackers/${id}/regenerate-code`, { method: 'POST' }),
    trackersRevoke: (id) => request(`/etechlog/trackers/${id}/revoke`, { method: 'POST' }),
    trackersDelete: (id) => request(`/etechlog/trackers/${id}`, { method: 'DELETE' }),

    // Checkouts list — status: 'active' | 'overdue' | 'all'
    checkouts: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return request(`/etechlog/checkouts${qs ? `?${qs}` : ''}`);
    },
  },
  iptUsers: {
    list: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return request(`/ipt-users${qs ? `?${qs}` : ''}`);
    },
    get: (cathayId) => request(`/ipt-users/${encodeURIComponent(cathayId)}`),
    create: (body) => request('/ipt-users', { method: 'POST', body }),
    patch: (cathayId, body) => request(`/ipt-users/${encodeURIComponent(cathayId)}`, { method: 'PATCH', body }),
    delete: (cathayId) => request(`/ipt-users/${encodeURIComponent(cathayId)}`, { method: 'DELETE' }),
  },
  imports: {
    devices: (file) => {
      const fd = new FormData();
      fd.append('file', file);
      return request('/imports/devices', { method: 'POST', body: fd, isFormData: true });
    },
    systemUsers: (file) => {
      const fd = new FormData();
      fd.append('file', file);
      return request('/imports/system-users', { method: 'POST', body: fd, isFormData: true });
    },
    iptUsers: (file) => {
      const fd = new FormData();
      fd.append('file', file);
      return request('/imports/ipt-users', { method: 'POST', body: fd, isFormData: true });
    },
    settings: (file) => {
      const fd = new FormData();
      fd.append('file', file);
      return request('/imports/settings', { method: 'POST', body: fd, isFormData: true });
    },
  },
  stats: {
    topPilots: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return request(`/stats/top-pilots${qs ? `?${qs}` : ''}`);
    },
    overview: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return request(`/stats/overview${qs ? `?${qs}` : ''}`);
    },
  },
  exports: {
    devicesCsvUrl: () => '/api/exports/devices.csv',
    systemUsersCsvUrl: () => '/api/exports/system-users.csv',
    iptUsersCsvUrl: () => '/api/exports/ipt-users.csv',
    settingsCsvUrl: () => '/api/exports/settings.csv',
  },
  sessions: {
    list: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return request(`/sessions${qs ? `?${qs}` : ''}`);
    },
    create: (body) => request('/sessions', { method: 'POST', body }),
    patch: (id, body) => request(`/sessions/${id}`, { method: 'PATCH', body }),
    delete: (id) => request(`/sessions/${id}`, { method: 'DELETE' }),
  },
  bookings: {
    list: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return request(`/bookings${qs ? `?${qs}` : ''}`);
    },
    inject: (body) => request('/bookings/inject', { method: 'POST', body }),
    patch: (id, body) => request(`/bookings/${id}`, { method: 'PATCH', body }),
    delete: (id) => request(`/bookings/${id}`, { method: 'DELETE' }),
  },
  uploads: {
    list: () => request('/uploads'),
    get: (id) => request(`/uploads/${id}`),
    commit: (id) => request(`/uploads/${id}/commit`, { method: 'POST' }),
    delete: (id) => request(`/uploads/${id}`, { method: 'DELETE' }),
    staged: () => request('/uploads/staged-bookings/all'),
    upload: (files, monthLabels) => {
      const fd = new FormData();
      for (const f of files) fd.append('files', f);
      fd.append('monthLabels', JSON.stringify(monthLabels));
      return request('/uploads', { method: 'POST', body: fd, isFormData: true });
    },
  },
  crossCheck: {
    run: (body) => request('/cross-check/runs', { method: 'POST', body }),
    runs: (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return request(`/cross-check/runs${qs ? `?${qs}` : ''}`);
    },
    flags: (runId, params = {}) => {
      const qs = new URLSearchParams(params).toString();
      return request(`/cross-check/runs/${runId}/flags${qs ? `?${qs}` : ''}`);
    },
    patchFlag: (id, body) => request(`/cross-check/flags/${id}`, { method: 'PATCH', body }),
    statsPilots: (month) => request(`/cross-check/stats/pilots?month=${encodeURIComponent(month)}`),
  },
  simulate: {
    summary: () => request('/simulate/summary'),
    sessions: (body) => request('/simulate/sessions', { method: 'POST', body }),
    reset: () => request('/simulate/reset', { method: 'DELETE' }),
    bookingsToSessions: (body) =>
      request('/simulate/bookings-to-sessions', { method: 'POST', body }),
  },
  settings: {
    public: () => request('/settings/public'),
    list: () => request('/settings'),
    patch: (key, value) => request(`/settings/${encodeURIComponent(key)}`, { method: 'PATCH', body: { value } }),
  },
  health: () => request('/health'),
  admin: {
    verifyPassword: (password) =>
      request('/admin/verify-password', { method: 'POST', body: { password } }),
    destroyCalendar: (body) =>
      request('/admin/destroy/calendar', { method: 'POST', body }),
    destroyIptUsers: (password) =>
      request('/admin/destroy/ipt-users', { method: 'POST', body: { password } }),
    destroySystemUsers: (password) =>
      request('/admin/destroy/system-users', { method: 'POST', body: { password } }),
    configExportUrl: () => '/api/admin/config-export',
    configImport: (settings, password) =>
      request('/admin/config-import', { method: 'POST', body: { settings, password } }),
    calendarBackupUrl: () => '/api/admin/calendar-backup',
    azureConfig: () => request('/admin/azure-config'),
    azureConfigPatch: (body) => request('/admin/azure-config', { method: 'PATCH', body }),
    /**
     * Fleet mTLS profile — one shared client cert for all iPads,
     * suitable for pushing to the whole fleet via MDM. GET returns the
     * current fingerprint + expiry (nulls if not yet issued). POST mints
     * a fresh cert and streams the .mobileconfig as a download. The
     * PKCS#12 password is returned in the X-IPTBC-P12-Password header
     * (one-shot).
     */
    mtlsFleetGet: () => request('/admin/mtls-fleet-profile'),
    mtlsFleetIssue: async () => {
      const res = await fetch('/api/admin/mtls-fleet-profile', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/x-apple-aspen-config',
          'X-Requested-With': 'fetch',
        },
      });
      if (!res.ok) {
        const text = await res.text();
        const e = new Error(text || `HTTP ${res.status}`);
        e.status = res.status;
        throw e;
      }
      const p12Password = res.headers.get('X-IPTBC-P12-Password') || '';
      const blob = await res.blob();
      return { blob, p12Password };
    },
  },
};

// Helper used by the multi-IPT upload flow: append per-file deviceIds along
// with monthLabels.
api.uploads.uploadWithDevices = (files, monthLabels, deviceIds) => {
  const fd = new FormData();
  for (const f of files) fd.append('files', f);
  fd.append('monthLabels', JSON.stringify(monthLabels));
  fd.append('deviceIds', JSON.stringify(deviceIds));
  return request('/uploads', { method: 'POST', body: fd, isFormData: true });
};
